import { Button, View } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import { useState, useRef, useEffect } from "react";

export function ExpandableView({
  initialHeight,
  children,
  className,
}: {
  initialHeight: number;
  children: ReactNode;
  className?: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(initialHeight);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      setContentHeight(contentRef.current?.scrollHeight ?? initialHeight);
    });
    resizeObserver.observe(contentRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const toggleContent = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <View
      className={cn(
        "HideShowContent relative mx-auto overflow-hidden",
        className,
      )}
    >
      <View
        className={cn("overflow-hidden transition duration-500 ease-in-out")}
        style={{ height: isExpanded ? "auto" : initialHeight }}
      >
        <div ref={contentRef} className="content">
          {children}
        </div>
      </View>
      {contentHeight > initialHeight && (
        <View className="absolute bottom-0 z-10 w-full bg-white">
          <Button
            variant="tertiary"
            color="CTA2"
            fullWidth="full"
            onClick={toggleContent}
            iconAlignment="end"
            iconName={isExpanded ? "caretUp" : "caretDown"}
            children={isExpanded ? "View Less" : "View More"}
          />
        </View>
      )}
    </View>
  );
}
